import { parallelPromiseHelper } from "@harvestr-api/utils";
import * as request from "request-promise";
import { getGitlabBearer } from "../helpers/gitlab-auth.helper";
import { AuthContext, gitlabStorageService } from "@harvestr-api/common";
import { gitlabApiService } from "./gitlab-api.service";

export const gitlabWebhookService = {
    async getProjectWebhooks(
        projectId: number,
        ctx: AuthContext,
        gotTokenAlready?: string,
        gotHostUrlAlready?: string
    ): Promise<any[]> {
        const { auth, host_url } = await getGitlabBearer(
            ctx,
            gotTokenAlready,
            gotHostUrlAlready
        );
        return request
            .get({
                url: `${host_url}/projects/${projectId}/hooks`,
                auth,
                qs: {},
                json: true,
                headers: { "user-agent": "node.js" }
            })
            .catch(err => {
                return Promise.resolve([]);
            });
    },

    async registerWebhook(
        projectId: number,
        projectName: string,
        ctx: AuthContext
    ) {
        try {
            const isWebhookAlreadyRegistered = await gitlabWebhookService.isWebhookAlreadyBound(
                projectId,
                `${process.env.INTEGRATIONS_API_URL}/devtools/gitlab/sync/issue?project_id=${ctx.project_id}`,
                ctx
            );
            if (isWebhookAlreadyRegistered === true) {
                console.log(
                    `gitlabWebhookService.registerWebhook() : hook already bound to issue's project name ' : ${
                        projectName || projectId
                    }`
                );
                return { statusCode: 204 };
            }
            if (isWebhookAlreadyRegistered === false) {
                const { auth, host_url } = await getGitlabBearer(ctx);
                request
                    .post({
                        url: `${host_url}/projects/${projectId}/hooks`,
                        auth,
                        body: {
                            id: projectId,
                            url: `${process.env.INTEGRATIONS_API_URL}/devtools/gitlab/sync/issue?project_id=${ctx.project_id}`,
                            issues_events: true,
                            push_events: false,
                            token: process.env.GITLAB_WEBHOOK_SIGNATURE
                        },
                        json: true,
                        headers: { "user-agent": "node.js" }
                    })
                    .catch(err => {
                        console.log(
                            "webhook catch promise : ",
                            JSON.stringify(err, null, 4)
                        );
                        return Promise.resolve();
                    });
            }
        } catch (error) {
            if (error.statusCode === 403) {
                console.log(
                    "The user oauth has not enough permissions to set a webhook)"
                );
            }
            return Promise.resolve({ statusCode: error.statusCode });
        }
    },

    async unregisterWebhook(
        where: { webhookId: number; projectId: number },
        ctx: AuthContext,
        gotTokenAlready?: string,
        gotHostUrlAlready?: string
    ) {
        const { auth, host_url } = await getGitlabBearer(
            ctx,
            gotTokenAlready,
            gotHostUrlAlready
        );

        return request
            .delete({
                url: `${host_url}/projects/${where.projectId}/hooks/${where.webhookId}`,
                auth,
                json: true,
                headers: { "user-agent": "node.js" }
            })
            .catch(err => {
                console.log(
                    "FAIL ON  : ",
                    `${host_url}/projects/${where.projectId}/hooks`
                );
                return Promise.resolve();
            });
    },

    async registerAllWebhooks(where: { project_id }) {
        const reposWithWebhookRegisteredOn = new Set<number>();
        const issuesWebhookLess = await gitlabStorageService.getIssuesOfProjectWithoutWebhook(
            where
        );
        await parallelPromiseHelper.uploadData(
            issuesWebhookLess || [],
            async issuesChunk => {
                return Promise.all(
                    issuesChunk.reduce((promises: any[], issue) => {
                        if (
                            !reposWithWebhookRegisteredOn.has(
                                issue.gitlabProjectId
                            )
                        ) {
                            promises.push(
                                gitlabWebhookService
                                    .registerWebhook(
                                        issue.gitlabProjectId,
                                        issue.gitlabProjectName as string,
                                        {
                                            type: "INTEGRATION_GITLAB",
                                            project_id: where.project_id
                                        }
                                    )
                                    .catch(e => {
                                        return null;
                                    })
                            );
                        }
                        reposWithWebhookRegisteredOn.add(issue.gitlabProjectId);
                        return promises;
                    }, [])
                );
            },
            10
        );
    },

    async unregisterAllWebhooks(
        ctx: AuthContext,
        gotTokenAlready?: string,
        gotHostUrlAlready?: string
    ) {
        // github exposes a API endpoint that provides all currently registered webhooks.
        // This helps us to not bypass any webhook due to a issue with webhookID reference broken.
        let webhooksToUnregister: any[] = [];

        const groups = (
            (await gitlabApiService.getGroups(
                ctx,
                gotTokenAlready,
                gotHostUrlAlready
            )) || []
        ).filter(group => group.request_access_enabled === true);

        for (const group of groups) {
            const allProjects = await gitlabApiService.getProjects(
                {
                    group_id: group.id
                },
                ctx,
                gotTokenAlready,
                gotHostUrlAlready
            );

            for (const project of allProjects) {
                const projectWebhooks = await gitlabWebhookService.getProjectWebhooks(
                    project.id,
                    ctx,
                    gotTokenAlready,
                    gotHostUrlAlready
                );

                webhooksToUnregister = [
                    ...webhooksToUnregister,
                    ...projectWebhooks.filter(webhook =>
                        webhook?.url?.includes(process.env.INTEGRATIONS_API_URL)
                    )
                ];
            }
        }

        webhooksToUnregister.length > 0 &&
            (await parallelPromiseHelper.uploadData(
                webhooksToUnregister,
                async webhooksChunk =>
                    Promise.all(
                        webhooksChunk.map(webhook => {
                            gitlabWebhookService.unregisterWebhook(
                                {
                                    webhookId: webhook.id,
                                    projectId: webhook.project_id
                                },
                                ctx,
                                gotTokenAlready,
                                gotHostUrlAlready
                            );
                        })
                    ),
                10
            ));

        return Promise.resolve();
    },

    isWebhookSignatureValid(event): boolean {
        return (
            event.headers["X-Gitlab-Token"] ===
            process.env.GITLAB_WEBHOOK_SIGNATURE
        );
    },

    async isWebhookAlreadyBound(
        projectId: number,
        url: string,
        ctx: AuthContext
    ): Promise<boolean> {
        const repoWebhooks = await gitlabWebhookService.getProjectWebhooks(
            projectId,
            ctx
        );
        if (repoWebhooks.length === 0) {
            return false;
        }
        const foundWebhook = (repoWebhooks || []).find(
            (wh: any) => wh.url.includes(url) && wh.issues_events === true
        );
        return foundWebhook !== undefined;
    }
};
